import { forwardRef } from 'react';

import { Helmet } from '@modern-js/runtime/head';
import { clsx } from 'clsx';

import type { Config } from '@crehana/analytics';
import { Breadcrumb } from '@crehana/compass.ui/breadcrumb';
import { Header, HeaderTitle } from '@crehana/compass.ui/header';
import { Tabs, TabsList, TabsListItem } from '@crehana/compass.ui/tabs';

import { type TNavigationData, useNavigate } from '@/shared/modern-router';

import { Container } from './container';

type TPageLayoutProps = {
  metadata?: {
    title?: string;
    description?: string;
  };
  renderAmplitudeEvent?: Config;
};

const PageLayout: React.FC<React.PropsWithChildren<TPageLayoutProps>> = ({
  children,
  metadata,
}) => {
  return (
    <>
      {metadata && (
        <Helmet>
          {metadata.title && <title>{metadata.title} | Crehana</title>}
          {metadata.description && (
            <meta name="description" content={metadata.description} />
          )}
        </Helmet>
      )}
      {children}
    </>
  );
};

type TPageLayoutTopProps = React.PropsWithChildren<
  React.ComponentProps<typeof Container>
> & {
  wrapperClassName?: string;
};
const PageLayoutTop = forwardRef<HTMLDivElement, TPageLayoutTopProps>(
  ({ className, wrapperClassName, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('tw-pt-3.2 tw-bg-neutral-light-100', wrapperClassName)}
      >
        <Container
          className={clsx('tw-space-y-2.4 !tw-mx-0', className)}
          {...props}
        />
      </div>
    );
  },
);
PageLayoutTop.displayName = 'PageLayoutTop';

type TPageLayoutBreadcrumbItem = {
  text: string;
  href?: string;
  url?: TNavigationData;
  onNavigate?: () => void;
};

type TPageLayoutBreadcrumbProps = {
  items: TPageLayoutBreadcrumbItem[];
};

const PageLayoutBreadcrumb = ({
  items,
  className,
}: TPageLayoutBreadcrumbProps & { className?: string }) => {
  const { navigateTo } = useNavigate();

  const adaptedItems = items.map(item => {
    if (item.url) {
      // If we don't cast the url to BuildUrlParameters<T> we will get an error as if it could be undefined, despite the previous validation
      const url = item.url;

      return {
        text: item.text,
        onNavigate: () => navigateTo(url),
      };
    }
    return item;
  });

  return (
    <div className={clsx(className)}>
      <Breadcrumb items={adaptedItems} />
    </div>
  );
};

const PageLayoutHeader = Header;

const PageLayoutTitle = HeaderTitle;

// Provisory: Need to update the compass.ui component because it was a <p> tag and we need to use a <div>
const PageLayoutSubtitle: React.FC<
  React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>
> = ({ className, ...props }) => {
  return (
    <div
      className={clsx(
        'tw-font-body4 tw-text-neutral-light-700 dark:tw-text-neutral-dark-600',
        className,
      )}
      {...props}
    />
  );
};

const PageLayoutContent: React.FC<
  React.PropsWithChildren<React.ComponentProps<typeof Container>>
> = ({ className, ...props }) => {
  return (
    <Container className={clsx('tw-mt-2.4 tw-mb-4', className)} {...props} />
  );
};

type TPageLayoutTabItem = {
  label: string;
  path: string;
  value: string;
  hide?: boolean;
};

type TPageLayoutTabsProps = {
  tabs: TPageLayoutTabItem[];
  currentTab: string;
  onTabChange: (value: string) => void;
  className?: string;
  rightContent?: React.ReactNode;
};

const PageLayoutTabs: React.FC<TPageLayoutTabsProps> = ({
  tabs,
  currentTab,
  onTabChange,
  className,
  rightContent,
}) => {
  return (
    <div
      className={clsx(
        'tw-bg-neutral-light-100 tw-sticky tw-top-0 tw-border-b tw-border-solid tw-border-neutral-light-400 tw-z-1',
        className,
      )}
    >
      <Tabs
        value={currentTab}
        onValueChange={onTabChange}
        activationMode="manual"
        className="tw-w-full"
      >
        <TabsList className="tw-h-4 tw-p-0 tw-flex tw-items-center tw-justify-between tw-w-full tw-px-2 sm:tw-px-2.4 md:tw-px-3.2 lg:tw-px-4.8">
          <div className="tw-flex tw-w-full tw-h-full tw-items-end">
            {tabs.map(tab => (
              <TabsListItem
                key={tab.value}
                value={tab.value}
                className={clsx(tab.hide && 'tw-hidden')}
              >
                {tab.label}
              </TabsListItem>
            ))}
          </div>
          {rightContent}
        </TabsList>
      </Tabs>
    </div>
  );
};

export {
  PageLayout,
  PageLayoutBreadcrumb,
  PageLayoutContent,
  PageLayoutHeader,
  PageLayoutSubtitle,
  PageLayoutTabs,
  PageLayoutTitle,
  PageLayoutTop,
};

export type { TPageLayoutTabItem };
